/**
 * CaseManagementService — live backend client.
 *
 * Talks to the SkyPlus BPM backend (/bpm/api/cases) with a Bearer token, exactly like
 * contract-workflow.service.ts. The public API is UNCHANGED from the previous mock version —
 * every method keeps its signature and return type — so the dashboard, list, detail (all 8
 * tabs) and the new-case wizard work without any component or SCSS change.
 *
 * The backend serialises dates as ISO strings; each response is revived back to JS `Date`
 * objects so templates that call `.getTime()` etc. behave identically to the old mock.
 */

import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { AuthenticationService, AppConfigService } from '@alfresco/adf-core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import {
  LegalCase, CaseParty, CaseDocument, CaseEvent, CaseTask,
  CaseActivity, CaseCost, CaseNote,
  CaseDashboardStats, CaseTypeBreakdown, CaseStatusBreakdown,
  CaseListFilter, NewCaseRequest
} from './case-management.model';

@Injectable({ providedIn: 'root' })
export class CaseManagementService {

  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthenticationService);
  private readonly appConfig = inject(AppConfigService);

  private readonly apiRoot =
    (this.appConfig.get<string>('skyplus.apiUrl', '/bpm/api') || '').replace(/\/+$/, '');
  private readonly base = `${this.apiRoot}/cases`;

  /** Live list for components that subscribe to it (kept for API compatibility). */
  readonly cases$ = new BehaviorSubject<LegalCase[]>([]);

  /** Assignees cache so getDistinctAssignees() can stay synchronous. */
  private assigneeCache: string[] = [];

  constructor() {
    this.http.get<string[]>(`${this.base}/assignees`, { headers: this.headers() })
      .pipe(catchError(() => of([] as string[])))
      .subscribe(a => this.assigneeCache = a ?? []);
  }

  private headers(): HttpHeaders {
    const t = this.auth.getToken();
    return t ? new HttpHeaders({ Authorization: `Bearer ${t}` }) : new HttpHeaders();
  }
  private jsonHeaders(): HttpHeaders { return this.headers().set('Content-Type', 'application/json'); }

  // ── Dashboard ────────────────────────────────────────────────────────────
  getDashboardStats(): Observable<CaseDashboardStats> {
    return this.http.get<CaseDashboardStats>(`${this.base}/dashboard/stats`, { headers: this.headers() });
  }

  getCaseTypeBreakdown(): Observable<CaseTypeBreakdown[]> {
    return this.http.get<CaseTypeBreakdown[]>(`${this.base}/dashboard/type-breakdown`, { headers: this.headers() });
  }

  getCaseStatusBreakdown(): Observable<CaseStatusBreakdown[]> {
    return this.http.get<CaseStatusBreakdown[]>(`${this.base}/dashboard/status-breakdown`, { headers: this.headers() });
  }

  getUpcomingEvents(daysAhead: number = 14): Observable<CaseEvent[]> {
    return this.http.get<any[]>(`${this.base}/dashboard/upcoming-events`,
      { headers: this.headers(), params: new HttpParams().set('daysAhead', daysAhead) })
      .pipe(map(list => (list || []).map(reviveEvent)));
  }

  getRecentActivity(limit: number = 10): Observable<CaseActivity[]> {
    return this.http.get<any[]>(`${this.base}/dashboard/recent-activity`,
      { headers: this.headers(), params: new HttpParams().set('limit', limit) })
      .pipe(map(list => (list || []).map(reviveActivity)));
  }

  getNeedsAttention(limit: number = 5): Observable<LegalCase[]> {
    return this.http.get<any[]>(`${this.base}/dashboard/needs-attention`,
      { headers: this.headers(), params: new HttpParams().set('limit', limit) })
      .pipe(map(list => (list || []).map(reviveCase)));
  }

  // ── Cases ────────────────────────────────────────────────────────────────
  listCases(filter?: CaseListFilter): Observable<LegalCase[]> {
    let params = new HttpParams();
    if (filter) {
      if (filter.searchTerm) { params = params.set('search', filter.searchTerm); }
      if (filter.status && filter.status !== 'ALL') { params = params.set('status', filter.status); }
      if (filter.type && filter.type !== 'ALL') { params = params.set('type', filter.type); }
      if (filter.priority && filter.priority !== 'ALL') { params = params.set('priority', filter.priority); }
      if (filter.assignedTo && filter.assignedTo !== 'ALL') { params = params.set('assignedTo', filter.assignedTo); }
      if (filter.hasUpcomingEvents) { params = params.set('hasUpcomingEvents', true); }
      if (filter.overdueOnly) { params = params.set('overdueOnly', true); }
    }
    return this.http.get<any[]>(this.base, { headers: this.headers(), params }).pipe(
      map(list => (list || []).map(reviveCase)),
      tap(list => {
        this.cases$.next(list);
        const merged = new Set([...this.assigneeCache, ...list.map(c => c.assignedTo).filter(Boolean)]);
        this.assigneeCache = Array.from(merged).sort();
      })
    );
  }

  getCase(id: string): Observable<LegalCase | null> {
    return this.http.get<any>(`${this.base}/${encodeURIComponent(id)}`, { headers: this.headers() })
      .pipe(map(reviveCase), catchError(() => of(null)));
  }

  // ── Case detail data ─────────────────────────────────────────────────────
  getParties(caseId: string): Observable<CaseParty[]> {
    return this.http.get<CaseParty[]>(`${this.base}/${encodeURIComponent(caseId)}/parties`, { headers: this.headers() });
  }

  getDocuments(caseId: string): Observable<CaseDocument[]> {
    return this.http.get<any[]>(`${this.base}/${encodeURIComponent(caseId)}/documents`, { headers: this.headers() })
      .pipe(map(list => (list || []).map(reviveDocument)));
  }

  getEvents(caseId: string): Observable<CaseEvent[]> {
    return this.http.get<any[]>(`${this.base}/${encodeURIComponent(caseId)}/events`, { headers: this.headers() })
      .pipe(map(list => (list || []).map(reviveEvent)));
  }

  getTasks(caseId: string): Observable<CaseTask[]> {
    return this.http.get<any[]>(`${this.base}/${encodeURIComponent(caseId)}/tasks`, { headers: this.headers() })
      .pipe(map(list => (list || []).map(reviveTask)));
  }

  getActivity(caseId: string): Observable<CaseActivity[]> {
    return this.http.get<any[]>(`${this.base}/${encodeURIComponent(caseId)}/activity`, { headers: this.headers() })
      .pipe(map(list => (list || []).map(reviveActivity)));
  }

  getCosts(caseId: string): Observable<CaseCost[]> {
    return this.http.get<any[]>(`${this.base}/${encodeURIComponent(caseId)}/costs`, { headers: this.headers() })
      .pipe(map(list => (list || []).map(reviveCost)));
  }

  getNotes(caseId: string): Observable<CaseNote[]> {
    return this.http.get<any[]>(`${this.base}/${encodeURIComponent(caseId)}/notes`, { headers: this.headers() })
      .pipe(map(list => (list || []).map(reviveNote)));
  }

  // ── Mutations ──────────────────────────────────────────────────────────────
  createCase(req: NewCaseRequest): Observable<LegalCase> {
    const body = {
      title: req.title, type: req.type, priority: req.priority, ourRole: req.ourRole,
      sourceRef: req.sourceRef, sourceType: req.sourceType, sourceDescription: req.sourceDescription,
      exposureAmount: req.exposureAmount, opposingParty: req.opposingParty, opposingCounsel: req.opposingCounsel,
      assignedTo: req.assignedTo, court: req.court, courtCaseNumber: req.courtCaseNumber,
      expectedCloseDate: toIso(req.expectedCloseDate),
      parties: (req.parties || []).map(p => ({
        role: p.role, type: p.type, name: p.name, idNumber: p.idNumber, contact: p.contact,
        email: p.email, address: p.address, representedBy: p.representedBy, notes: p.notes
      })),
      firstEvent: req.firstEvent ? {
        title: req.firstEvent.title, type: req.firstEvent.type,
        scheduledAt: toIso(req.firstEvent.scheduledAt), durationMinutes: req.firstEvent.durationMinutes,
        location: req.firstEvent.location, reminderDaysBefore: req.firstEvent.reminderDaysBefore
      } : null,
      notes: req.notes
    };
    return this.http.post<any>(this.base, body, { headers: this.jsonHeaders() }).pipe(map(reviveCase));
  }

  // ── Documents (Alfresco) ───────────────────────────────────────────────────
  /** Upload a workstation file into the case's Alfresco folder (optional document-type label). */
  uploadDocument(ref: string, file: File, label?: string): Observable<CaseDocument> {
    const form = new FormData();
    form.append('file', file);
    if (label) { form.append('label', label); }
    return this.http.post<any>(`${this.base}/${encodeURIComponent(ref)}/documents/upload`, form,
      { headers: this.headers() }).pipe(map(reviveDocument));
  }

  /** Link existing repository node(s) and/or every file in a repository folder to the case. */
  linkDocuments(ref: string, body: { nodeIds?: string[]; folderId?: string; label?: string }): Observable<CaseDocument[]> {
    return this.http.post<any[]>(`${this.base}/${encodeURIComponent(ref)}/documents/link`, body,
      { headers: this.jsonHeaders() }).pipe(map(list => (list || []).map(reviveDocument)));
  }

  // ── In-tab add actions (phase 4) ────────────────────────────────────────────
  addParty(ref: string, body: Partial<CaseParty>): Observable<CaseParty> {
    return this.http.post<CaseParty>(`${this.base}/${encodeURIComponent(ref)}/parties`, body, { headers: this.jsonHeaders() });
  }
  addNote(ref: string, body: { content: string; isPrivileged?: boolean }): Observable<CaseNote> {
    return this.http.post<any>(`${this.base}/${encodeURIComponent(ref)}/notes`, body, { headers: this.jsonHeaders() }).pipe(map(reviveNote));
  }
  addTask(ref: string, body: { description: string; assignee?: string; dueDate?: string; priority?: string }): Observable<CaseTask> {
    return this.http.post<any>(`${this.base}/${encodeURIComponent(ref)}/tasks`, body, { headers: this.jsonHeaders() }).pipe(map(reviveTask));
  }
  addEvent(ref: string, body: { title: string; type?: string; scheduledAt?: string; durationMinutes?: number; location?: string; reminderDaysBefore?: number[] }): Observable<CaseEvent> {
    return this.http.post<any>(`${this.base}/${encodeURIComponent(ref)}/events`, body, { headers: this.jsonHeaders() }).pipe(map(reviveEvent));
  }
  addCost(ref: string, body: { date?: string; description?: string; type?: string; amount?: number; paidTo?: string; reference?: string }): Observable<CaseCost> {
    return this.http.post<any>(`${this.base}/${encodeURIComponent(ref)}/costs`, body, { headers: this.jsonHeaders() }).pipe(map(reviveCost));
  }

  // ── Reference data + user search (reused platform services) ──────────────────
  /** A configurable Reference Data list (e.g. case-source-types / case-courts / case-document-types). */
  referenceItems(listKey: string): Observable<{ value: string; label: string }[]> {
    return this.http.get<any>(`${this.apiRoot}/wf/reference-data/${encodeURIComponent(listKey)}`, { headers: this.headers() })
      .pipe(map(r => (r?.items || []).map((i: any) => ({ value: i.value, label: i.label }))), catchError(() => of([])));
  }

  /** Search platform users (for the Assigned-to picker) — reuses the existing user search endpoint. */
  searchUsers(term: string): Observable<{ id: string; displayName: string; email?: string }[]> {
    const params = new HttpParams().set('term', term).set('maxItems', 20);
    return this.http.get<any>(`${this.apiRoot}/users/search`, { headers: this.headers(), params })
      .pipe(map(r => (r?.users || r || []) as any[]),
            map(list => list.map(u => ({ id: u.id ?? u.username ?? u.displayName, displayName: u.displayName ?? u.username ?? u.id, email: u.email }))),
            catchError(() => of([])));
  }

  // ── Filter helpers ───────────────────────────────────────────────────────
  getDistinctAssignees(): string[] { return this.assigneeCache; }
}

// ── Date revival (ISO string → Date) — keeps the component contract identical ──
function d(v: any): any { return v ? new Date(v) : undefined; }

function reviveCase(c: any): LegalCase {
  return { ...c,
    openedDate: d(c.openedDate), expectedCloseDate: d(c.expectedCloseDate),
    closedDate: d(c.closedDate), nextEventDate: d(c.nextEventDate) };
}
function reviveDocument(x: any): CaseDocument { return { ...x, uploadedAt: d(x.uploadedAt) }; }
function reviveEvent(x: any): CaseEvent { return { ...x, scheduledAt: d(x.scheduledAt), createdAt: d(x.createdAt) }; }
function reviveTask(x: any): CaseTask { return { ...x, dueDate: d(x.dueDate), createdAt: d(x.createdAt), completedAt: d(x.completedAt) }; }
function reviveActivity(x: any): CaseActivity { return { ...x, timestamp: d(x.timestamp) }; }
function reviveCost(x: any): CaseCost { return { ...x, date: d(x.date) }; }
function reviveNote(x: any): CaseNote { return { ...x, createdAt: d(x.createdAt) }; }

function toIso(v: any): string | null {
  if (!v) { return null; }
  return v instanceof Date ? v.toISOString() : String(v);
}
